import '../../styles/templates/index.scss'; // ?[REVISIT] critical
// @ts-expect-error
import { load } from '@shopify/theme-sections';
import { debounce } from 'lodash-es';
import { domElementFromRawHTML, updateGeotargetedElements } from '../utils/dom';
import { IS_NATIVE_APP_CLIENT, poll, tryCatchWrapper } from '../utils/helpers';
import { contentLoaded } from '../utils/document-promises';
import { globalWindow } from '../utils/constants';
import { nativeBackupCouponApplier } from '../utils/autoCouponApplicator';
import pTry from 'p-try';
import {
  fireGA4HomepageAssetViewEvent,
  fireGA4HomepageAssetClickEvent,
  initHomepageScrollTrackingNativeApp,
} from '../utils/gtm-data-layer';

// ? [REVISIT] what does this do?
load('*');

const runHomePageScripts = async ({ previewMode = false } = {}) => {
  await contentLoaded;

  handleOutOfVariationsSectionRendering();
  const metatag = document.querySelector('body meta[itemprop="signup_offer_backup"]') as HTMLMetaElement;
  const code = metatag?.content;
  if (code) nativeBackupCouponApplier(code);

  await tryCatchWrapper(() =>
    updateGeotargetedElements({
      selector: 'main .shopify-section [geotarget_is_enabled="true"]',
    })
  );
  await adjustHomepageCarouselsPostGeoTarget();
  initCarousels();
  startAutoplayVideoObserver();
  initializeBuilderCarousel();
  if (!previewMode) {
    initAssignHeapAttributes();
  }
  initMobileCategoriesTracking();
  if (IS_NATIVE_APP_CLIENT) {
    initHomepageScrollTrackingNativeApp();
  }
};

const handleOutOfVariationsSectionRendering = async () => {
  window['optimizely'] = window['optimizely'] || [];

  await poll(() => window.optimizely.initialized)
    .then(() => {
      const outOfVariationSection = document.querySelectorAll('section.out-of-variation');
      if (
        window.home_page_settings?.experimentID &&
        !window.optimizely.get('state').getExperimentStates()[window.home_page_settings.experimentID]?.isActive &&
        outOfVariationSection.length > 0
      ) {
        // Render default section for users bucketed out of the A and B variations
        renderOutOfBucketSection(outOfVariationSection);
      }
    })
    .catch((e) => {
      console.log(e);
      console.log('Optimizely initialization timeout');
      const outOfVariationSection = document.querySelectorAll('section.out-of-variation');
      renderOutOfBucketSection(outOfVariationSection);
    });
};

const renderOutOfBucketSection = (outOfVariationSection: NodeListOf<HTMLElement>) => {
  outOfVariationSection.forEach((section) => {
    section.classList.remove('out-of-variation');
    section.classList.remove('ab-test-variation');
  });
};

/**
 * Adjusts the 'active' slide & creates carousel indicators based on remaining slides
 */
const adjustHomepageCarouselsPostGeoTarget = async () => {
  const carousels = document.querySelectorAll(`[data-identifier="hp-hero-carousel"]`);

  carousels.forEach((carousel) => {
    let slides = Array.from(carousel.querySelectorAll('.carousel-item'));
    if (carousel.id.includes('carousel-mobile')) {
      if (IS_NATIVE_APP_CLIENT) {
        slides = filterOutCarouselSlides(slides, 'carousel-item-onlyweb');
      } else {
        slides = filterOutCarouselSlides(slides, 'carousel-item-onlynativeapp');
      }
    }

    const slideAlreadyActive = slides.some((item) => item.classList.contains('active'));
    if (!slideAlreadyActive && slides[0]) slides[0].classList.add('active');

    if (slides.length > 1) {
      const parentNode = carousel.querySelector('.carousel-indicators');
      if (parentNode) {
        removeAllChildren(parentNode);
        Array.from({ length: slides.length }).forEach((_, index) => {
          const indicator = domElementFromRawHTML(
            `<li data-target="#${carousel.id}" data-slide-to="${index}" class="${index === 0 ? 'active' : ''}"></li>`
          );
          parentNode.appendChild(indicator);
        });
      }
      carousel.classList.add('carousel');
      window?.$?.(carousel).carousel({ ride: 'carousel', interval: 5000 });
    } else {
      const prevControl = carousel.querySelector('.carousel-control-prev');
      const nextControl = carousel.querySelector('.carousel-control-next');
      if (prevControl && nextControl) {
        prevControl.style.visibility = 'hidden';
        nextControl.style.visibility = 'hidden';
      }
    }
  });
};

function filterOutCarouselSlides(slides: HTMLElement[], unwantedClassName: string) {
  return slides.filter((item) => {
    if (item.classList.contains(unwantedClassName)) {
      item.remove(); // removes slide from the DOM
      return false; // removes slide from the array
    } else {
      item.classList.remove('initially-hidden'); // prevent flash of forbidden content
    }
    return true;
  });
}

function assignHeapAttributes() {
  const sections = [...document.querySelectorAll('.shopify-section .hp-section')];
  const visibleSections = sections.filter(
    (section) =>
      window.getComputedStyle(section, null).getPropertyValue('display') !== 'none' &&
      !section.classList.contains('mobile-categories-list')
  );
  const anchors = visibleSections.map((section) => [...section.querySelectorAll('a')]);

  anchors.forEach((anchor, i) =>
    anchor.forEach((a) => {
      if (!a.classList.contains('controls')) {
        a.setAttribute('track-section-index', (i + 1).toString());
        const trackType = a.getAttribute('track-type');
        const trackItemIndex = a.getAttribute('track-item-index');
        a.setAttribute('testid', `${trackType}-${trackItemIndex}-${i + 1}`);
      }
    })
  );
}

const debouncedAssignHeapAttributes = debounce(assignHeapAttributes, 150);

function initAssignHeapAttributes() {
  assignHeapAttributes();
  window.addEventListener('resize', debouncedAssignHeapAttributes);
}

// HP-Hero section
function initCarousels() {
  window?.$?.('.carousel').carousel({
    interval: 5000,
  });
  const ctaButtons = [...document.querySelectorAll('.hero-button')];
  ctaButtons.forEach((button) => button.addEventListener('mouseover', pauseCarousel));
  ctaButtons.forEach((button) => button.addEventListener('mouseout', cycleCarousel));
}
type HeroSectionScriptsArgType = { id: string; isCaptionsBelow: boolean };

globalWindow.runHeroSectionScripts = function ({ isCaptionsBelow, id }: HeroSectionScriptsArgType) {
  if (isCaptionsBelow) {
    placeDots(id);
    let width = document.documentElement.clientWidth;
    window.addEventListener('resize', () => {
      debouncedPlaceDots(id, width);
      width = document.documentElement.clientWidth;
    });
  }
  hideControls(id);
  initSlideshowTracking(id);
};

function initSlideshowTracking(id: string) {
  const section = 'hp-hero-slideshow';
  const carousel = document.querySelector(`#shopify-section-${id} [data-identifier="hp-hero-carousel"]`);
  const slides = [...document.querySelectorAll<Element>(`#shopify-section-${id} .carousel-item`)];

  if (slides.length === 0) return;

  const getSlideLink = (slide: Element) => slide.querySelector<HTMLAnchorElement>('a[track-name]');

  const getPayload = (link: HTMLAnchorElement, index: number) => ({
    asset_name: link.getAttribute('track-name') ?? '',
    section,
    position: index + 1,
    page_type: 'homepage' as const,
  });

  const fireSlideViewGA4Event = (slide: Element, index: number) => {
    const link = getSlideLink(slide);
    if (!link) return;
    const payload = getPayload(link, index);
    if (!payload.asset_name) return;
    fireGA4HomepageAssetViewEvent(payload);
  };

  // Guard against duplicate initialization (e.g. Shopify designMode shopify:section:load).
  const slideBoundAttr = 'data-slide-tracking-bound';
  const isAlreadyBound = carousel?.hasAttribute(slideBoundAttr);

  if (!isAlreadyBound) {
    const activeIndex = slides.findIndex((slide) => slide.classList.contains('active'));
    const activeSlide = slides[activeIndex];
    if (activeIndex >= 0 && activeSlide) fireSlideViewGA4Event(activeSlide, activeIndex);
  }

  // Fire view on each subsequent slide transition via MutationObserver (no jQuery dependency).
  if (carousel && !isAlreadyBound) {
    carousel.setAttribute(slideBoundAttr, '1');
    let prevActiveIndex = slides.findIndex((slide) => slide.classList.contains('active'));
    const slideObserver = new MutationObserver(() => {
      const nextActiveIndex = slides.findIndex((slide) => slide.classList.contains('active'));
      const nextActiveSlide = slides[nextActiveIndex];
      if (nextActiveIndex >= 0 && nextActiveSlide && nextActiveIndex !== prevActiveIndex) {
        prevActiveIndex = nextActiveIndex;
        fireSlideViewGA4Event(nextActiveSlide, nextActiveIndex);
      }
    });
    slides.forEach((slide) => slideObserver.observe(slide, { attributes: true, attributeFilter: ['class'] }));
  }

  const clickBoundAttr = 'data-click-tracking-bound';
  slides.forEach((slide, index) => {
    const link = getSlideLink(slide);
    if (!link || link.hasAttribute(clickBoundAttr)) return;
    link.setAttribute(clickBoundAttr, '1');
    link.addEventListener('click', () =>
      fireGA4HomepageAssetClickEvent({ ...getPayload(link, index), destination_url: link.href })
    );
  });
}

const debouncedPlaceDots = debounce((id, width) => {
  placeDots(id, width);
}, 150);

function placeDots(id: string, width = 0) {
  if (document.documentElement.clientWidth < 768 && document.documentElement.clientWidth !== width) {
    const dots = document.querySelector(`#shopify-section-${id} ol.controls`);
    const image = document.querySelector(`#shopify-section-${id} .carousel-item.active img`);
    const imageHeight = image?.clientHeight;
    if (dots && imageHeight) dots.style.top = `${imageHeight - 30}px`;
  }
}
function hideControls(id: string) {
  const slides = [...document.querySelectorAll(`#shopify-section-${id} .carousel-item`)];
  const controls = [...document.querySelectorAll(`#shopify-section-${id} .controls`)];
  if (slides.length === 1) {
    controls.forEach((control) => (control.style.display = 'none'));
  }
}

function pauseCarousel() {
  window?.$?.('.carousel').carousel('pause');
}
function cycleCarousel() {
  window?.$?.('.carousel').carousel('cycle');
}

function initMobileCategoriesTracking() {
  const section = 'hp-mobile-categories-section';
  const items = [...document.querySelectorAll<HTMLAnchorElement>('.mobile-categories-list a[track-name]')];

  if (items.length === 0) return;

  const itemIndexMap = new Map(items.map((item, i) => [item, i]));
  const viewedItems = new Set<number>();

  const getPayload = (item: HTMLAnchorElement, index: number) => ({
    asset_name: item.getAttribute('track-name') ?? '',
    section,
    position: index + 1,
    page_type: 'homepage' as const,
  });

  // Click tracking — guard against duplicate bindings on re-initialization (e.g. Shopify designMode).
  const Click_Bound_attr = 'data-click-tracking-bound';
  items.forEach((item, index) => {
    if (item.hasAttribute(Click_Bound_attr)) return;
    item.setAttribute(Click_Bound_attr, '1');
    item.addEventListener('click', () =>
      fireGA4HomepageAssetClickEvent({ ...getPayload(item, index), destination_url: item.href })
    );
  });

  // View tracking — only create a new observer for unobserved items.
  const Observed_Attr = 'data-view-tracking-bound';
  const unobservedItems = items.filter((item) => !item.hasAttribute(Observed_Attr));
  if (unobservedItems.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const item = entry.target as HTMLAnchorElement;
        const index = itemIndexMap.get(item);
        if (index === undefined || viewedItems.has(index)) return;
        viewedItems.add(index);
        fireGA4HomepageAssetViewEvent(getPayload(item, index));
        if (viewedItems.size === items.length) observer.disconnect();
      });
    },
    { threshold: 0.1 }
  );

  unobservedItems.forEach((item) => {
    item.setAttribute(Observed_Attr, '1');
    observer.observe(item);
  });
}

// Image grid section
function initImageGridLayout(cards: HTMLElement[]) {
  if (cards.length === 3) {
    cards.forEach((card) => (card.style.width = '33.3%'));
  }
}

function initImageGridTracking(sectionId: string, cards: HTMLElement[]) {
  // Guard against duplicate bindings on re-initialization (e.g. Shopify designMode shopify:section:load).
  const sectionRoot = document.querySelector(`#shopify-section-${sectionId}`);
  const Initialized_Attr = 'data-gtm-tracking-initialized';
  if (!sectionRoot || sectionRoot.hasAttribute(Initialized_Attr)) return;
  sectionRoot.setAttribute(Initialized_Attr, '1');

  const section = 'hero-image-grid-section';
  const cardIndexMap = new Map(cards.map((card, i) => [card, i]));
  const viewedAssets = new Set<number>();

  const getPayload = (card: HTMLElement, index: number) => ({
    asset_name: card.getAttribute('track-name') ?? '',
    section,
    position: index + 1,
    page_type: 'homepage' as const,
  });

  // Click tracking
  cards.forEach((card, index) => {
    card.addEventListener('click', () =>
      fireGA4HomepageAssetClickEvent({
        ...getPayload(card, index),
        destination_url: (card as HTMLAnchorElement).href ?? '',
      })
    );
  });
  // View tracking
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const card = entry.target as HTMLElement;
        const index = cardIndexMap.get(card);
        if (index === undefined || viewedAssets.has(index)) return;
        viewedAssets.add(index);
        fireGA4HomepageAssetViewEvent(getPayload(card, index));
        if (viewedAssets.size === cards.length) observer.disconnect();
      });
    },
    { threshold: 0.1 }
  );

  cards.forEach((card) => observer.observe(card));
}

globalWindow.runImageGridSectionScripts = function ({ sectionId }: { sectionId: string }) {
  if (!sectionId) return;

  const cards = [...document.querySelectorAll<HTMLElement>(`#shopify-section-${sectionId} .image-grid__card`)].filter(
    Boolean
  );
  if (cards.length === 0) return;
  initImageGridLayout(cards);
  initImageGridTracking(sectionId, cards);
};

// Full-width video section
// Play the video only if the video in user's viewport.

let autoplayVideoIntersectionObserverRef: IntersectionObserver | undefined;
function startAutoplayVideoObserver() {
  // cleanup existing observer to allow this function to be re-runable
  if (autoplayVideoIntersectionObserverRef) {
    autoplayVideoIntersectionObserverRef.disconnect();
  }

  const options = {
    root: null,
    threshold: 0.7,
  };
  autoplayVideoIntersectionObserverRef = new IntersectionObserver(callBack, options);
  const videos = [...document.querySelectorAll('video.fw-video__video')];
  function callBack(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      if (entry.intersectionRatio > 0.7) {
        (entry.target as HTMLVideoElement).play();
      } else {
        (entry.target as HTMLVideoElement).pause();
      }
    });
  }

  videos.forEach((video) => autoplayVideoIntersectionObserverRef?.observe(video));
}

runHomePageScripts();

const shopifyDesignModeEventHandler = (name: string) => (...eventData: unknown[]) => {
  // console.log({name, eventData});
  runHomePageScripts({ previewMode: true });
};
if (window?.Shopify?.designMode) {
  // ? add back additional events as necessary
  const events = [
    'shopify:section:load',
    // 'shopify:section:unload',
    // 'shopify:section:select',
    // 'shopify:section:deselect',
    // 'shopify:section:reorder',
    // 'shopify:block:select',
    // 'shopify:block:deselect',
  ];
  events.forEach((name) => {
    document.addEventListener(name, shopifyDesignModeEventHandler(name));
  });
}

// move to utils? do it w/o a while loop
const removeAllChildren = (node: HTMLElement) => {
  while (node.firstChild) {
    node.removeChild(node.firstChild);
  }
};

// HomePage Pixlee Feed
const lazyLoadHomePagePixleeFeed = () => {
  const mountNode = document.getElementById('PixleeFeedHP');
  if (!mountNode) return;

  const observer = new IntersectionObserver(
    (entries, observer) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        import(
          /* webpackChunkName: "PixleeFeedHPrenderLazy" */
          './_shared/pixlee/PixleeFeedHPrenderLazy'
        ).then(({ renderPixleeFeedHP }) => {
          observer.disconnect();
          renderPixleeFeedHP(mountNode);
        });
      }
    },
    { root: null, rootMargin: '500px 0px 500px 0px', threshold: 0 }
  );
  if (mountNode.parentElement) observer.observe(mountNode.parentElement);
};

lazyLoadHomePagePixleeFeed();

const lazyLoadHomepageReactCarousel = () => {
  const mountNodes = document.querySelectorAll('.react-carousel-container');
  if (mountNodes.length === 0) return;

  mountNodes.forEach((mountNode) => {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          import(
            /* webpackChunkName: "HomepageReactCollectionCarouselRenderLazy" */
            './_shared/HomepageReactCarousel/HomepageReactCarouselRenderLazy'
          ).then(({ renderHomepageReactCollectionCarousel }) => {
            observer.disconnect();
            renderHomepageReactCollectionCarousel(mountNode as HTMLElement);
          });
        }
      },
      { root: null, rootMargin: '500px 0px 500px 0px', threshold: 0 }
    );
    if (mountNode.parentElement) observer.observe(mountNode.parentElement);
  });
};

lazyLoadHomepageReactCarousel();

const initializeBuilderCarousel = () => {
  const mountNodes = document.querySelectorAll('.builder-carousel-component');

  if (mountNodes.length > 0) {
    pTry(async () => {
      import('./homepage/initBuilderHomepage').then(({ initBuilderHomepage }) => {
        initBuilderHomepage(mountNodes);
      });
    });
  }
};
